import { useState, useRef, useEffect, Fragment } from "react";
import httpf from "../../utils/frontend/httpf";
import PropTypes from "prop-types";
import Input from "./Input";
import { CrossIcon, PercentageIcon } from "./Icons";
import cls from "classnames";
import lottie from "lottie-web";
import lottie_confetti2 from "../../../public/assets/lottie-animations/lottie-confetti-2.json";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";

const Coupon = ({
  state,
  prepareResponse,
  onValidation,
  className = "",
  contentClassName = "",
  variant = Coupon.VARIANTS.labelFirst,
  viewOnly = false,
  applyTextColor,
  applyHoverTextColor,
  isLightTheme
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [isInputMode, setIsInputMode] = useState(
    variant === Coupon.VARIANTS.inputFirst ? true : false
  );

  const [couponValidationMessage, setCouponValidationMessage] = useState(
    "Have a Coupon code?"
  );

  const [coupon, setCoupon] = useState("");

  useEffect(() => {
    setIsInputMode(
      state?.isInputMode !== undefined ? state.isInputMode : isInputMode
    );
    setCouponValidationMessage(
      state?.couponValidationMessage !== undefined
        ? state.couponValidationMessage
        : couponValidationMessage
    );
    setCoupon(state?.coupon !== undefined ? state.coupon : coupon);
  }, [state]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (coupon === "") return;
    
    setIsValidating(true);
    const responsePayload = prepareResponse(coupon);
    try {
      const response = await httpf.post(
        "/api/validate-coupon/",
        responsePayload
      );
      setIsValidating(false);
      onValidation &&
        onValidation({
          coupon: coupon,
          couponValidationMessage: response.data.apply_text,
          isInputMode: false,
          data: response.data,
        });
      setCouponValidationMessage(response.data.apply_text);
      setIsInputMode(false);
      lottie.loadAnimation({
        container: container.current,
        animationData: lottie_confetti2,
        autoplay: true,
        loop: false,
        renderer: "svg",
      });
      setTimeout(lottie.destroy, 1500);
    } catch (error) {
      setIsValidating(false);
      toast.error(
        error?.message || "Invalid Coupon"
      );
    }
  };

  const resetToDefault = () => {
    setCoupon("");
    setIsInputMode(variant === Coupon.VARIANTS.inputFirst ? true : false);
    setCouponValidationMessage("Have a Coupon code?");
    onValidation &&
      onValidation({
        coupon: "",
        isInputMode: variant === Coupon.VARIANTS.inputFirst ? true : false,
        couponValidationMessage: "Have a Coupon code?",
        data: undefined,
      });
  };

  const container = useRef(null);

  if (viewOnly && coupon === "") return null;

  return (
    <div
      className={cls(
        "w-full flex justify-center items-center text-left !max-w-none min-w-0 min-h-[1.25rem] relative",
        className
      )}
    >
      <div
        ref={container}
        className="absolute w-[min(95vw,48rem)] h-[48rem] pointer-events-none z-50 bottom-0 md:bottom-auto"
      ></div>
      <div
        className={cls("!max-w-none w-full relative", {
          "bg-[#2A2E31] rounded-lg dark:bg-white": variant === Coupon.VARIANTS.inputFirst,
        }, contentClassName)}
      >
        <Transition
          show={isInputMode || (variant === Coupon.VARIANTS.inputFirst && coupon === "")}
          as={Fragment}
          enter="transition ease-out duration-250"
          enterFrom="transform opacity-0 scale-90"
          enterTo="transform opacity-100 scale-100"
          leave="duration-[0]"
        >
          <Input
            className="w-full dark:text-[#161A1D]"
            value={coupon}
            onSubmit={handleSubmit}
            onChange={setCoupon}
            isValidating={isValidating}
            variant={variant === Coupon.VARIANTS.inputFirst ? Input.VARIANTS.box : Input.VARIANTS.underline}
            isLightTheme={isLightTheme}
            applyTextColor={applyTextColor}
            applyHoverTextColor={applyHoverTextColor}
          />
        </Transition>
        {!isInputMode && coupon === "" && variant === Coupon.VARIANTS.labelFirst && (
          <div
            className={cls(
              "w-full flex gap-2 items-center justify-center text-sm select-none",
              "cursor-pointer hover",
              isLightTheme ? "text-white hover:text-[rgba(255,255,255,.8)]" : "text-[#70C3FF] hover:text-[#97d0f9] dark:text-[#208ecd] dark:hover:text-[#5a97b9]",
              )}
            onClick={() => setIsInputMode(true)}
          >
            <PercentageIcon className="w-4 h-4 stroke-current dark:stroke-[#868686]" />
            <span>{couponValidationMessage}</span>
          </div>
        )}
        <Transition
          show={!isInputMode && coupon !== ""}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="transform opacity-0 scale-90"
          enterTo="transform opacity-100 scale-100"
          leave="duration-[0]"
        >
          <div className="w-full flex items-center gap-2 relative px-2 py-3 rounded-lg bg-gradient-to-r from-[#2F373D] to-[#22272B] text-[#6FDA79] dark:bg-none dark:bg-[#F2F8F3]">
            <PercentageIcon className="w-[1.125rem] h-[1.125rem] flex-shrink-0 stroke-current dark:stroke-[#148D4C]" />
            <div className="overflow-hidden text-ellipsis w-[calc(100%)] whitespace-nowrap select-none dark:text-[#148D4C]">
              <span className="uppercase font-bold text-[0.8125rem]">
                {coupon}
              </span>
              <span className="text-xs">{` - ${couponValidationMessage}`}</span>
            </div>
            <div
              className="absolute top-0 right-0 translate-x-[35%] translate-y-[-35%] self-start cursor-pointer text-opacity-80 text-white bg-[#323941] dark:bg-[#898989] h-5 w-5 flex items-center justify-center rounded-full"
              onClick={resetToDefault}
            >
              <CrossIcon className="w-3 h-3 stroke-current" />
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default Coupon;

Coupon.VARIANTS = {
  labelFirst: "label-first",
  inputFirst: "input-first"
}

Coupon.prototype = {
  state: PropTypes.object.isRequired,
  prepareResponse: PropTypes.func.isRequired,
  onValidation: PropTypes.func.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf([Coupon.VARIANTS.inputFirst, Coupon.VARIANTS.labelFirst]),
};
